<template>
    <div class="priority-select-wrapper">
        <span class="priority-label">Приоритет</span>
        <v-tooltip v-if="disabled && tooltip" bottom max-width="300">
            <template v-slot:activator="{ on, attrs }">
                <div
                    class="priority-box priority-box--disabled"
                    :class="{ 'priority-box--with-dropdown': showDropdownIcon }"
                    v-bind="attrs"
                    v-on="on"
                    @click.stop
                >
                    <span v-if="displayValue === 'none'" class="priority-box--disabled__box">✕</span>
                    <span v-else class="priority-number priority-number--disabled">{{ displayValue }}</span>
                    <v-icon
                        v-if="showDropdownIcon"
                        class="priority-dropdown-icon priority-dropdown-icon--disabled"
                        size="16"
                    >mdi-chevron-down</v-icon>
                </div>
            </template>
            <span style="white-space: pre-line;">{{ tooltip }}</span>
        </v-tooltip>
        <v-menu
            v-else
            ref="menu"
            v-model="menuOpen"
            :close-on-content-click="true"
            offset-y
            origin="top left"
            transition="slide-y-transition"
            :content-class="menuContentClass"
            :nudge-width="0"
            :nudge-top="-1"
            :nudge-left="0"
            @input="handleMenuToggle"
        >
            <template v-slot:activator="{ on, attrs }">
                <div
                    ref="activatorBox"
                    class="priority-box"
                    :class="{
                        'priority-box--with-dropdown': showDropdownIcon,
                        'priority-box--open': menuOpen,
                    }"
                    v-bind="attrs"
                    v-on="on"
                    @click.stop
                >
                    <span v-if="displayValue === 'none'" class="priority-box__box">✕</span>
                    <span v-else class="priority-number">{{ displayValue }}</span>
                    <v-icon
                        v-if="showDropdownIcon"
                        class="priority-dropdown-icon"
                        size="16"
                    >mdi-chevron-down</v-icon>
                </div>
            </template>
            <v-card class="priority-menu" flat>
                <v-list dense>
                    <v-list-item
                        @click="handleSelect('none')"
                        :class="{ 'priority-selected': displayValue === 'none' }"
                    >
                        <v-list-item-content>
                            <div class="priority-menu-item">
                                <v-icon v-if="!showDropdownIcon" small color="darkNew" class="priority-x-icon">mdi-close</v-icon>
                                <span v-else class="priority-menu-none-char">×</span>
                            </div>
                        </v-list-item-content>
                    </v-list-item>
                    <v-list-item
                        v-for="i in availablePriorities"
                        :key="i"
                        @click="handleSelect(i)"
                        :class="{ 'priority-selected': displayValue === i }"
                    >
                        <v-list-item-content>
                            <div class="priority-menu-item">
                                <span class="priority-number">{{ i }}</span>
                            </div>
                        </v-list-item-content>
                    </v-list-item>
                </v-list>
            </v-card>
        </v-menu>
    </div>
</template>

<script>
export default {
    name: 'PrioritySelect',
    props: {
        value: {
            type: Number,
            default: null,
        },
        maxPriority: {
            type: Number,
            default: 0,
        },
        excludedPriorities: {
            type: Array,
            default: () => [],
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        tooltip: {
            type: String,
            default: '',
        },
        showDropdownIcon: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        menuOpen: false,
    }),
    computed: {
        displayValue() {
            if (this.value === null || this.value === undefined || this.value === 'none') {
                return 'none'
            }
            return this.value
        },
        menuContentClass() {
            const classes = ['priority-menu-content']
            if (this.showDropdownIcon) {
                classes.push('priority-menu-content--cascade')
            }
            return classes.join(' ')
        },
        availablePriorities() {
            if (this.maxPriority === 0) {
                return [1]
            }

            if (this.displayValue === 'none') {
                const maxAvailable = this.maxPriority + 1
                return Array.from({ length: maxAvailable }, (_, i) => i + 1)
            }

            return Array.from({ length: this.maxPriority }, (_, i) => i + 1)
        },
    },
    watch: {
        availablePriorities() {
            if (this.menuOpen) {
                this.$nextTick(() => this.syncMenuWidth())
            }
        },
        menuOpen(isOpen) {
            if (isOpen) {
                this.$nextTick(() => this.syncMenuWidth())
            }
        },
    },
    methods: {
        handleMenuToggle(isOpen) {
            if (isOpen) {
                this.$nextTick(() => this.syncMenuWidth())
            }
        },
        syncMenuWidth() {
            const activator = this.$refs.activatorBox
            const content = this.$refs.menu?.$refs?.content
            if (!activator || !content) {
                return
            }

            const width = Math.round(activator.getBoundingClientRect().width)
            content.style.setProperty('--priority-menu-width', `${width}px`)
            content.style.width = `${width}px`
            content.style.minWidth = `${width}px`
            content.style.maxWidth = `${width}px`
        },
        handleSelect(value) {
            if (value === 'none') {
                this.$emit('input', null)
            } else {
                this.$emit('input', Number(value))
            }
        },
    },
}
</script>

<style lang="sass" scoped>
@import '@/vars.sass'

.priority-select-wrapper
    display: flex
    align-items: center
    justify-content: center
    gap: 8px

.priority-label
    font-size: 14px
    color: #666
    white-space: nowrap
    margin-right: 4px

    @media (max-width: 890px)
        display: none

.priority-box
    width: 36px
    height: 36px
    border: 1px solid #E0E0E0
    border-radius: 4px
    display: flex
    align-items: center
    justify-content: center
    cursor: pointer
    background-color: #fff
    transition: border-color 0.2s ease, background-color 0.2s ease, border-radius 0.2s ease

    &:hover
        border-color: #999
        background-color: #f5f5f5

.priority-box--with-dropdown
    position: relative
    justify-content: center
    width: 46px
    height: 32px
    padding: 0 12px 0 0
    border-color: #e3e7ee
    border-radius: 8px

    &:hover
        border-color: #e3e7ee
        background-color: #fff

.priority-box--open
    border-color: $main-new
    border-bottom-left-radius: 0
    border-bottom-right-radius: 0
    background-color: #fff

    &:hover
        border-color: $main-new
        background-color: #fff

.priority-box--open:not(.priority-box--with-dropdown)
    box-shadow: 0 0 0 3px rgba(82, 90, 213, 0.12)

.priority-box--with-dropdown.priority-box--open
    box-shadow: none

.priority-dropdown-icon
    position: absolute
    right: 4px
    top: 50%
    transform: translateY(-50%)
    flex-shrink: 0
    color: rgba(0, 0, 0, 0.54) !important
    pointer-events: none
    transition: transform 0.2s ease

.priority-box--with-dropdown .priority-dropdown-icon
    right: 3px
    font-size: 14px !important
    color: $muted-new !important

.priority-box--open .priority-dropdown-icon
    transform: translateY(-50%) rotate(180deg)

.priority-dropdown-icon--disabled
    color: #bbb !important

.priority-box--disabled
    cursor: not-allowed
    background-color: #f5f5f5
    border-color: #E0E0E0

    &:hover
        border-color: #E0E0E0
        background-color: #f5f5f5

.priority-number
    font-size: 14px
    font-weight: 500
    color: $main-new

.priority-box--with-dropdown .priority-number,
.priority-box--with-dropdown .priority-box__box
    width: 100%
    font-family: $font-family-base
    font-size: 13px
    font-weight: 800
    line-height: 1
    text-align: center
    color: $dark-new

.priority-number--disabled
    color: #999

.priority-x-icon
    font-size: 18px

.priority-menu-none-char
    font-size: 13px
    font-weight: 800
    line-height: 1
    color: #666

.priority-menu-item
    display: flex
    align-items: center
    justify-content: center
    width: 100%
    padding: 4px 0

.priority-selected
    background-color: #E3F2FD

::v-deep .priority-menu .v-list-item
    min-height: 36px
    padding: 0

::v-deep .priority-menu .v-list-item__content
    padding: 0

::v-deep .v-tooltip__content
    max-width: 300px !important
    white-space: pre-line !important
    text-align: left !important
</style>

<!-- Teleported v-menu content lives outside scoped tree — global styles required -->
<style lang="sass">
@import '@/vars.sass'

.v-menu__content.priority-menu-content
    --priority-menu-width: 36px
    width: var(--priority-menu-width) !important
    min-width: var(--priority-menu-width) !important
    max-width: var(--priority-menu-width) !important
    padding: 0 !important
    margin-top: 0 !important
    background: #fff !important
    border: 1px solid $main-new
    border-top: none !important
    border-radius: 0 0 4px 4px
    box-shadow: 0 6px 16px rgba(26, 26, 46, 0.1)
    overflow: hidden !important
    contain: none !important

.v-menu__content.priority-menu-content--cascade
    border-radius: 0 0 8px 8px

.v-menu__content.priority-menu-content .priority-menu
    width: 100% !important
    min-width: 100% !important
    max-width: 100% !important
    border: none !important
    border-radius: 0 !important
    box-shadow: none !important
    background: #fff !important

.v-menu__content.priority-menu-content .v-list
    width: 100% !important
    padding: 0 !important
    background: #fff !important

.v-menu__content.priority-menu-content .v-list-item
    width: 100% !important
    min-height: 36px !important
    padding: 0 !important
    margin: 0 !important

.v-menu__content.priority-menu-content .v-list-item__content
    width: 100% !important
    padding: 0 !important

.v-menu__content.priority-menu-content .priority-menu-item
    width: 100% !important

.v-menu__content.priority-menu-content--cascade .priority-number,
.v-menu__content.priority-menu-content--cascade .priority-menu-none-char
    font-family: $font-family-base
    font-size: 13px
    font-weight: 800
    color: $dark-new
</style>
